import React, { useState } from 'react';
import AiUsageStats from './AiUsageStats';
import ApiKeyManager from './ApiKeyManager';
import ApiUsageMonitor from './ApiUsageMonitor';

function ApiDashboard({ apiList, apiFilter, setApiFilter, apiKeyStatus, apiKeyMapping, handleStatusClick, activeTooltip, setActiveTooltip }) {
  const [activeTab, setActiveTab] = useState('ai-usage');

  const tabs = [
    { id: 'ai-usage', label: 'AI 사용량', icon: '📊' },
    { id: 'api-keys', label: 'API 키 관리', icon: '🔑' },
    { id: 'api-monitor', label: 'API 모니터링', icon: '📈' },
    { id: 'api-endpoints', label: 'API 엔드포인트', icon: '🔗' }
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* 탭 네비게이션 */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 py-2 px-4 border-none rounded-lg text-sm cursor-pointer transition-all duration-200 min-h-[44px] whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-blue-500 text-white font-medium'
                : 'bg-transparent text-gray-500 hover:bg-slate-50'
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="text-base">{tab.icon}</span>
            <span className="text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* 탭 내용 */}
      <div className="min-h-[400px]">
        {activeTab === 'ai-usage' && <AiUsageStats />}

        {activeTab === 'api-keys' && <ApiKeyManager />}

        {activeTab === 'api-monitor' && <ApiUsageMonitor />}

        {activeTab === 'api-endpoints' && (
          <div className="bg-slate-50 rounded-xl p-6 md:p-4">
            <div className="flex justify-between items-center flex-wrap gap-4 mb-6 md:flex-col md:items-start">
              <h2 className="text-xl font-semibold text-gray-900 m-0">API 엔드포인트 목록</h2>
              <div className="flex gap-1 flex-wrap md:w-full md:justify-start">
                <button
                  className={`py-1 px-2 border rounded-md text-xs cursor-pointer transition-all duration-200 ${
                    apiFilter === 'all'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-slate-200 text-gray-500 hover:border-blue-500 hover:text-blue-500'
                  }`}
                  onClick={() => setApiFilter('all')}
                >
                  전체 ({apiList.length})
                </button>
                <button
                  className={`py-1 px-2 border rounded-md text-xs cursor-pointer transition-all duration-200 ${
                    apiFilter === 'completed'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-slate-200 text-gray-500 hover:border-blue-500 hover:text-blue-500'
                  }`}
                  onClick={() => setApiFilter('completed')}
                >
                  완료 ({apiList.filter(a => a.status === 'completed').length})
                </button>
                <button
                  className={`py-1 px-2 border rounded-md text-xs cursor-pointer transition-all duration-200 ${
                    apiFilter === 'needs-key'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-slate-200 text-gray-500 hover:border-blue-500 hover:text-blue-500'
                  }`}
                  onClick={() => setApiFilter('needs-key')}
                >
                  API키 필요 ({apiList.filter(a => a.status === 'needs-key').length})
                </button>
                <button
                  className={`py-1 px-2 border rounded-md text-xs cursor-pointer transition-all duration-200 ${
                    apiFilter === 'pending'
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-slate-200 text-gray-500 hover:border-blue-500 hover:text-blue-500'
                  }`}
                  onClick={() => setApiFilter('pending')}
                >
                  대기 ({apiList.filter(a => a.status === 'pending').length})
                </button>
              </div>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden">
                <thead>
                  <tr>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">카테고리</th>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">메소드</th>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">엔드포인트</th>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">설명</th>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">상태</th>
                    <th className="py-2 px-4 text-left border-b border-slate-200 bg-slate-50 font-semibold text-xs text-gray-500 uppercase">우선순위</th>
                  </tr>
                </thead>
                <tbody>
                  {apiList
                    .filter(api => {
                      if (apiFilter === 'all') return true;
                      if (apiFilter === 'completed') return api.status === 'completed';
                      if (apiFilter === 'needs-key') return api.status === 'needs-key';
                      if (apiFilter === 'pending') return api.status === 'pending';
                      return true;
                    })
                    .map((api) => (
                      <tr
                        key={api.id}
                        className="hover:bg-slate-50"
                        onMouseEnter={() => setActiveTooltip(api.id)}
                        onMouseLeave={() => setActiveTooltip(null)}
                      >
                        <td className="py-2 px-4 text-sm border-b border-slate-200">
                          <span className="inline-block py-0.5 px-2 rounded text-[11px] font-medium">
                            {api.category}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm border-b border-slate-200">
                          <span className={`inline-block py-0.5 px-2 rounded text-[11px] font-medium text-white ${
                            api.method.toLowerCase() === 'get' ? 'bg-emerald-500' :
                            api.method.toLowerCase() === 'post' ? 'bg-blue-500' :
                            api.method.toLowerCase() === 'put' ? 'bg-amber-500' :
                            api.method.toLowerCase() === 'delete' ? 'bg-red-500' : 'bg-gray-500'
                          }`}>
                            {api.method}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm border-b border-slate-200 relative">
                          <code className="bg-slate-100 py-0.5 px-1.5 rounded text-xs">{api.endpoint}</code>
                          {activeTooltip === api.id && api.tooltip && (
                            <div className="absolute top-full left-0 z-[100] bg-slate-800 text-white p-3 rounded-lg text-xs min-w-[250px] max-w-[400px] shadow-xl">
                              <pre className="m-0 whitespace-pre-wrap font-mono">{api.tooltip}</pre>
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-4 text-sm border-b border-slate-200">
                          <div className="flex flex-col gap-1">
                            {api.description}
                            {api.note && <span className="text-[11px] text-gray-400">{api.note}</span>}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-sm border-b border-slate-200">
                          {apiKeyMapping[api.id] ? (
                            <button
                              className={`py-1 px-3 border-none rounded-md text-xs cursor-pointer min-h-[32px] ${
                                apiKeyStatus[apiKeyMapping[api.id]]
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                              onClick={() => handleStatusClick(api)}
                            >
                              {apiKeyStatus[apiKeyMapping[api.id]]
                                ? '✅ 키 설정됨'
                                : '🔑 API키 필요'}
                            </button>
                          ) : (
                            <span className="inline-block py-0.5 px-2 rounded text-[11px] font-medium">
                              {api.status === 'completed' ? '✅ 완료' : api.status === 'pending' ? '⏳ 대기' : '🔧 진행중'}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-sm border-b border-slate-200">
                          <span className={`inline-block py-0.5 px-2 rounded text-[11px] font-medium ${
                            api.priority === 'high' ? 'bg-red-100 text-red-800' :
                            api.priority === 'medium' ? 'bg-amber-100 text-amber-800' :
                            'bg-indigo-100 text-indigo-800'
                          }`}>
                            {api.priority === 'high' ? '높음' : api.priority === 'medium' ? '중간' : '낮음'}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* 카테고리별 요약 */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold mb-4">카테고리별 현황</h3>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-2">
                {['인증', '사용자', 'AI', '커뮤니티', '파일', '알림'].map(category => {
                  const categoryApis = apiList.filter(a => a.category === category);
                  const completed = categoryApis.filter(a => a.status === 'completed').length;
                  const total = categoryApis.length;
                  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

                  return (
                    <div key={category} className="bg-white py-2 px-4 rounded-lg border border-slate-200">
                      <div className="flex justify-between mb-1">
                        <span className="font-medium text-xs">{category}</span>
                        <span className="text-xs text-gray-500">{completed}/{total}</span>
                      </div>
                      <div className="h-1 bg-slate-200 rounded overflow-hidden mb-1">
                        <div
                          className="h-full bg-blue-500 rounded transition-[width] duration-300"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-[11px] text-gray-400">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ApiDashboard;
